import { Link, Theme, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useAuth } from 'components/AuthProvider'
import DefaultLayout from 'components/layout/DefaultLayout'
import { voucherReviewPath } from 'components/Routes/VoucherFlowRoutes'
import { myVaultPath, walletPath } from 'components/Routes/Routes'
import { useEffect, useState } from 'react'
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'
import { convertCamelCaseToWords } from '../../../utils/util'

const useStyles = makeStyles()((theme: Theme) => ({
  header: {
    textAlign: 'center',
    color: theme.palette.primary.main,
    fontSize: '3rem',
    marginTop: theme.spacing(4)
  },
  status: {
    textAlign: 'center',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    fontSize: '3rem'
  },
  message: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  link: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    color: theme.palette.primary.main
  }
}))

export default function BlinkPayTransferStatus(): JSX.Element {
  const { state } = useLocation()
  const { setToast } = useAuth()
  const { classes } = useStyles()
  const navigate = useNavigate()

  const status = convertCamelCaseToWords(state?.status ?? 'N/A')
  const message = state?.message

  const voucherForm = localStorage.getItem('voucher_form')
  // Pay-for-order: when settlement already bought the gold, the stored return URL
  // points at the checkout screen the customer paid from — where the order is now
  // spent and "Complete Trade" is the obvious next click. Send them to their vault to
  // see what they bought instead of back to a screen that invites paying twice.
  const purchaseExecuted = Boolean(state?.purchaseExecuted)

  // This deposit was funding a checkout order (TransactionButtons stored it on the
  // way out). Settlement buys the gold server-side, so there is NOTHING for the
  // customer to do on the way back — but a bank payment is often still clearing when
  // they return, so the purchase has not executed YET.
  //
  // Sending them back to checkout in that window is what Cam filmed on 11 Aug: the
  // screen still reads "YOU'LL NEED TO PAY", the BlinkPay button is live, and tapping
  // it returns "This order already has a payment in progress — check your wallet
  // before paying again". They had paid; the screen asked them to pay again and then
  // refused to let them.
  const linkedOrderId = localStorage.getItem('blinkpay_review_order_id')
  const awaitingLinkedPurchase = Boolean(linkedOrderId) && !purchaseExecuted

  const redirectURL =
    purchaseExecuted || awaitingLinkedPurchase
      ? null
      : localStorage.getItem('url_after_blinkpay_payment')

  const [showWalletLink] = useState(() => !redirectURL && !voucherForm)

  useEffect(() => {
    if (purchaseExecuted) {
      localStorage.removeItem('url_after_blinkpay_payment')
      localStorage.removeItem('blinkpay_review_order_id')
      setToast({
        message: 'Payment complete — your gold has been purchased.',
        open: true,
        duration: 5000,
        type: 'success'
      })
      navigate(myVaultPath())
      return
    }
    if (awaitingLinkedPurchase) {
      // Paid, still clearing. Say so, and do NOT return them to the screen that
      // asks for the money again. The purchase completes on settlement without
      // them — the 15-minute poller finishes it if the bank is slow.
      localStorage.removeItem('url_after_blinkpay_payment')
      localStorage.removeItem('blinkpay_review_order_id')
      setToast({
        message:
          'Payment received. We are completing your purchase — your gold will ' +
          'appear in your vault shortly.',
        open: true,
        duration: 8000,
        type: 'info'
      })
      navigate(myVaultPath())
      return
    }
    if (redirectURL || voucherForm) {
      const toastType: 'success' | 'error' | 'info' =
        state?.status === 'processing'
          ? 'info'
          : state?.success
          ? 'success'
          : 'error'
      setToast({
        message: state?.message,
        open: true,
        duration: 5000,
        type: toastType
      })
      if (redirectURL) {
        localStorage.removeItem('url_after_blinkpay_payment')
        navigate(redirectURL)
      } else if (voucherForm) {
        localStorage.removeItem('voucher_form')
        navigate(voucherReviewPath(), {
          state: JSON.parse(voucherForm ?? '')
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <DefaultLayout backgroundColor='white'>
      <Typography variant='h1' component='h1' className={classes.header}>
        BLINKPAY PAYMENT
      </Typography>
      <Typography variant='h2' className={classes.status}>
        {status}
      </Typography>
      {message && (
        <Typography variant='body1' className={classes.message}>
          {message}
        </Typography>
      )}
      {showWalletLink && (
        <Typography variant='body1' className={classes.link}>
          <Link component={RouterLink} to={walletPath()} underline='always'>
            Go back to wallet
          </Link>
        </Typography>
      )}
    </DefaultLayout>
  )
}
